import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type Route } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter } from './firestoreUtils.js';

const ROUTES = 'routes';

const converter = buildConverter<Route>(
  (data) => data as Route,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreRouteRepository {
  async findAll(): Promise<Route[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, ROUTES), where('environment', '==', getCurrentEnvironment())).withConverter(
      converter
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<Route | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, ROUTES, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<Route[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, ROUTES),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(route: Route): Promise<Route> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(route.id);
    const data = { ...route, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, ROUTES, route.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, ROUTES, route.id).withConverter(converter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, ROUTES, id));
  }
}

export const createFirestoreRouteRepository = (): FirestoreRouteRepository => new FirestoreRouteRepository();
